import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Switch } from "@shared/components/ui/switch";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { useTranslation } from "react-i18next";
import { useEffect, useCallback } from "react";
import { AlertCircle } from "lucide-react";
import { useCityValidation } from "./useCityValidation";

export default function CityForm({
  city,
  handleChange,
  setCity,
  errors: externalErrors,
  setErrors: setExternalErrors,
}) {
  const { t } = useTranslation();
  const {
    errors: validationErrors,
    validateField,
    validateForm,
    clearFieldError,
  } = useCityValidation();

  // Use external errors if provided, otherwise use validation errors
  const displayErrors = externalErrors || validationErrors;

  const handleInputChange = useCallback(
    (e) => {
      const { id, value } = e.target;
      handleChange(e);

      // Clear any existing error for this field
      clearFieldError(id);

      // Validate the field with a small delay to avoid excessive validation
      setTimeout(() => {
        validateField(id, value);
      }, 300);
    },
    [handleChange, clearFieldError, validateField]
  );

  const handleNumberChange = useCallback(
    (e) => {
      const { id, value } = e.target;
      const numericValue = value === "" ? "" : parseFloat(value);
      setCity({ ...city, [id]: numericValue });

      // Clear any existing error for this field
      clearFieldError(id);

      // Validate the field
      setTimeout(() => {
        validateField(id, value);
      }, 300);
    },
    [city, setCity, clearFieldError, validateField]
  );

  // Validate entire form when city data changes
  useEffect(() => {
    if (city && Object.keys(city).length > 0) {
      const validationResult = validateForm(city);

      // If external setErrors is provided, update it with validation results
      if (setExternalErrors && !validationResult.isValid) {
        setExternalErrors(validationResult.errors);
      }
    }
  }, [city, validateForm, setExternalErrors]);

  // Export validation function for parent components
  useEffect(() => {
    // Attach validation function to the component for external access
    if (typeof window !== "undefined") {
      window.validateCityForm = () => {
        if (city) {
          return validateForm(city);
        }
        return {
          isValid: false,
          data: null,
          errors: { general: "No city data" },
        };
      };
    }

    return () => {
      if (typeof window !== "undefined") {
        delete window.validateCityForm;
      }
    };
  }, [city, validateForm]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label
            htmlFor="name"
            className={displayErrors.name ? "text-red-600" : ""}
          >
            {t("city.name")} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            value={city?.name || ""}
            onChange={handleInputChange}
            className={displayErrors.name ? "border-red-500" : ""}
            placeholder={t("city.namePlaceholder")}
          />
          {displayErrors.name && (
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {displayErrors.name}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="nameArabic"
            className={displayErrors.nameArabic ? "text-red-600" : ""}
          >
            {t("city.arabicName")} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="nameArabic"
            value={city?.nameArabic || ""}
            onChange={handleInputChange}
            className={displayErrors.nameArabic ? "border-red-500" : ""}
            placeholder={t("city.arabicNamePlaceholder")}
            dir="rtl"
          />
          {displayErrors.nameArabic && (
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {displayErrors.nameArabic}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="deadline"
            className={displayErrors.deadline ? "text-red-600" : ""}
          >
            {t("common.deadline")} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="deadline"
            type="text"
            value={city?.deadline || ""}
            onChange={handleInputChange}
            className={displayErrors.deadline ? "border-red-500" : ""}
            placeholder="24/48 hours"
          />
          {displayErrors.deadline && (
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {displayErrors.deadline}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="deliveryFees"
            className={displayErrors.deliveryFees ? "text-red-600" : ""}
          >
            {t("common.fees.deliveryFees")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id="deliveryFees"
            type="number"
            min="0"
            step="0.01"
            value={city?.deliveryFees || ""}
            onChange={handleNumberChange}
            className={displayErrors.deliveryFees ? "border-red-500" : ""}
            placeholder="30"
          />
          {displayErrors.deliveryFees && (
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {displayErrors.deliveryFees}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="refusalFees"
            className={displayErrors.refusalFees ? "text-red-600" : ""}
          >
            {t("common.fees.refusalFees")}
          </Label>
          <Input
            id="refusalFees"
            type="number"
            min="0"
            step="0.01"
            value={city?.refusalFees || ""}
            onChange={handleNumberChange}
            className={displayErrors.refusalFees ? "border-red-500" : ""}
            placeholder="0"
          />
          {displayErrors.refusalFees && (
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {displayErrors.refusalFees}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="cancellationFees"
            className={displayErrors.cancellationFees ? "text-red-600" : ""}
          >
            {t("common.fees.cancellationFees")}
          </Label>
          <Input
            id="cancellationFees"
            type="number"
            min="0"
            step="0.01"
            value={city?.cancellationFees || ""}
            onChange={handleNumberChange}
            className={displayErrors.cancellationFees ? "border-red-500" : ""}
            placeholder="0"
          />
          {displayErrors.cancellationFees && (
            <div className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {displayErrors.cancellationFees}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="hub">{t("city.hub")}</Label>
          <Input
            id="hub"
            value={city?.hub || ""}
            onChange={handleInputChange}
            placeholder={t("city.hubPlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="zone">{t("city.zone")}</Label>
          <Input
            id="zone"
            value={city?.zone || ""}
            onChange={handleInputChange}
            placeholder={t("city.zonePlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="delai_de_livraison">
            {t("city.delaiDeLivraison")}
          </Label>
          <Input
            id="delai_de_livraison"
            value={city?.delai_de_livraison || ""}
            onChange={handleInputChange}
            placeholder="24H"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="jour">{t("city.jour")}</Label>
          <Input
            id="jour"
            value={city?.jour || ""}
            onChange={handleInputChange}
            placeholder={t("city.jourPlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ville">{t("city.ville")}</Label>
          <Input
            id="ville"
            value={city?.ville || ""}
            onChange={handleInputChange}
            placeholder={t("city.villePlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="sortie_par_semaine">
            {t("city.sortieParSemaine")}
          </Label>
          <Input
            id="sortie_par_semaine"
            value={city?.sortie_par_semaine || ""}
            onChange={handleInputChange}
            placeholder={t("city.sortieParSemainePlaceholder")}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Switch
          id="isFeesFixed"
          checked={Boolean(city?.isFeesFixed) || false}
          onCheckedChange={(checked) =>
            setCity({ ...city, isFeesFixed: checked })
          }
        />
        <Label htmlFor="isFeesFixed" className="text-sm">
          {t("common.fees.fixedFees")}
        </Label>
      </div>

      {Object.keys(displayErrors).length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t("validation.pleaseFixErrors")}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
